import { useSettings } from "api/general";
import { useRoles } from "api/roles";
import { useStores } from "api/stores";
import { useTranslations } from "api/translations";
import { useWallets } from "api/wallets";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { LayoutComponent } from "components/Layout/LayoutComponent";
import { isEmpty } from "lodash";
import { PermissionRestrictedLayout } from "permissions/components/PermissionRestrictedLayout";
import { useEffect } from "react";
import { selectRoles, setACL } from "reduxState/ACL/ACLSlice";
import { setAdminData } from "reduxState/adminData/adminDataSlice";
import {
  selectCurrentStoreData,
  setCurrentStoreData,
} from "reduxState/currentStoreData/currentStoreDataSlice";
import { setDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { selectIsLoggedIn } from "reduxState/login/loginSlice";
import {
  setCountries,
  setCurrencies,
  setIdentificationMethods,
  setTenantSettings,
  setTimezones,
} from "reduxState/settings/settings";
import { setTranslations } from "reduxState/translations/translationsSlice";
import { ListResponse } from "types/listing";
import { StoreData } from "types/stores";
import { hasAccessToCurrentTenant } from "utilities/tenantAccess";

export interface LayoutInterceptorProps {
  component: React.ComponentType;
  layout?: React.ComponentType<any>;
}

export const LayoutInterceptor = ({
  component,
  layout: Layout = LayoutComponent,
}: LayoutInterceptorProps) => {
  const Component = component;
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const store = useAppSelector(selectCurrentStoreData);
  // Admin's ACL are ready, loaded and parsed in Redux
  const arePermissionsLoaded = !isEmpty(useAppSelector(selectRoles));
  const {
    useAdminData,
    useTenantSettings,
    useCountries,
    useIdentificationMethods,
    useTimezones,
    useCurrencies,
  } = useSettings();
  const { data: tenantSettings } = useTenantSettings();
  const { data: adminData } = useAdminData();
  const { useWalletTypeList } = useWallets();
  const { data: walletTypeList } = useWalletTypeList({
    filters: [
      {
        condition: "eq",
        query: "isDefault",
        value: true,
      },
    ],
  });
  const { useTranslationsList } = useTranslations();
  const { data: translations } = useTranslationsList();
  const isStoreDataAlreadyFetched = !!(store && store.id);
  const { useInitialStoreData, useStoreList } = useStores();
  const { data: storeList } = useStoreList();
  const storeData = useInitialStoreData(store.id, isStoreDataAlreadyFetched);
  const { useResources } = useRoles();
  const { data: resources } = useResources() as { data: ListResponse };
  const { data: countries } = useCountries();
  const { data: identificationMethods } = useIdentificationMethods();
  const { data: timezones } = useTimezones();
  const { data: currencies } = useCurrencies();
  // Effect for checking tenant access and changing it if needed
  useEffect(() => {
    // Only execute when we have admin data and store list
    if (adminData && storeList?.items?.length) {
      const currentAdminStores: string[] = adminData?.roles[0]?.stores || [];

      // Check if admin has access to the current store using our helper function
      const hasUserAccessToCurrentStore = hasAccessToCurrentTenant();

      // If admin doesn't have access to any store, do nothing
      if (currentAdminStores.length === 0) {
        return;
      }

      // If admin does NOT have access to current store but has access to other stores
      if (!hasUserAccessToCurrentStore) {
        const firstAccessibleStore = currentAdminStores[0];

        const targetStoreObject = storeList.items.find(
          (s: StoreData) => s.code === firstAccessibleStore,
        );

        if (targetStoreObject) {
          // Force tenant change
          dispatch(
            setCurrentStoreData({
              code: targetStoreObject.code,
              currency: targetStoreObject.currency,
              name: targetStoreObject.name,
              id: targetStoreObject.storeId,
            }),
          );
        }
      }
    }
  }, [adminData, storeList, store.code, dispatch]);

  // Original effect for store data initialization
  useEffect(() => {
    if (!isStoreDataAlreadyFetched && storeData) {
      // Initialize store data
      dispatch(
        setCurrentStoreData({
          code: storeData?.code,
          currency: storeData?.currency,
          name: storeData?.name,
          id: storeData?.storeId,
        }),
      );
    }
  }, [storeData, dispatch, isStoreDataAlreadyFetched]);

  useEffect(() => {
    dispatch(setTenantSettings(tenantSettings?.settings));
  }, [tenantSettings, dispatch]);

  useEffect(() => {
    dispatch(setCountries(countries?.choices));
  }, [countries, dispatch]);

  useEffect(() => {
    dispatch(setIdentificationMethods(identificationMethods?.choices));
  }, [identificationMethods, dispatch]);

  useEffect(() => {
    dispatch(setTimezones(timezones?.choices));
  }, [timezones, dispatch]);

  useEffect(() => {
    dispatch(setCurrencies(currencies?.choices));
  }, [currencies, dispatch]);

  useEffect(() => {
    dispatch(
      setAdminData({
        email: adminData?.email,
        username: adminData?.username,
        firstName: adminData?.firstName,
        lastName: adminData?.lastName,
        roles: adminData?.roles,
      }),
    );
    dispatch(
      setACL({
        roles: adminData?.roles?.[0],
        resources: resources?.items,
      }),
    );
  }, [adminData, dispatch, resources]);

  useEffect(() => {
    dispatch(
      setDefaultWalletData({
        code: walletTypeList?.items?.[0]?.code,
        name: walletTypeList?.items?.[0]?.name,
        unitPluralName: walletTypeList?.items?.[0]?.unitPluralName,
        unitSingularName: walletTypeList?.items?.[0]?.unitSingularName,
      }),
    );
  }, [walletTypeList, dispatch]);

  useEffect(() => {
    dispatch(setTranslations({ translations: translations?.items || [] }));
  }, [translations, dispatch]);

  if (isLoggedIn && arePermissionsLoaded) {
    return (
      <PermissionRestrictedLayout>
        <Layout>
          <Component />
        </Layout>
      </PermissionRestrictedLayout>
    );
  } else {
    return null;
  }
};
